import React, { useContext, useEffect, useState, useCallback, useRef } from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  Badge,
  IconButton,
  Popover,
  Box,
  Typography,
  List,
  CircularProgress,
  Tooltip,
} from "@material-ui/core";
import AssignmentTurnedInIcon from "@material-ui/icons/AssignmentTurnedIn";
import { useHistory } from "react-router-dom";

import useMyActivities from "../../hooks/useMyActivities";
import ActivityNotificationItem from "../ActivityNotificationItem";
import { AuthContext } from "../../context/Auth/AuthContext";
import { SocketContext } from "../../context/Socket/SocketContext";
import { i18n } from "../../translate/i18n";

const useStyles = makeStyles((theme) => ({
  iconButton: {
    color: "white",
    width: 34,
    height: 34,
    padding: 6,
    borderRadius: "50%",
    margin: theme.spacing(0, 0.4),
    position: "relative",
    "&:hover": {
      backgroundColor: "rgba(255, 255, 255, 0.12)",
    },
  },
  iconButtonLabel: {
    color: "white",
  },
  badge: {
    "& .MuiBadge-badge": {
      backgroundColor: "#f44336 !important",
      color: "white",
      fontWeight: 700,
      boxShadow: "0 2px 4px rgba(244, 67, 54, 0.4)",
      borderRadius: 8,
      fontSize: "0.65rem",
      height: 16,
      minWidth: 16,
      padding: "0 3px",
      right: 3,
      top: 3,
    },
  },
  popoverPaper: {
    width: 380,
    maxHeight: 480,
    overflow: "hidden",
    borderRadius: theme.spacing(1),
    boxShadow: theme.shadows[2],
    display: "flex",
    flexDirection: "column",
  },
  header: {
    padding: theme.spacing(2),
    backgroundColor: theme.palette.background.paper,
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  headerTitle: {
    fontWeight: 600,
    fontSize: "1rem",
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  list: {
    padding: 0,
    overflowY: "auto",
    flex: 1,
    "&::-webkit-scrollbar": { width: "8px" },
    "&::-webkit-scrollbar-track": {
      backgroundColor: theme.palette.background.default,
      borderRadius: "4px",
    },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: theme.palette.action.disabled,
      borderRadius: "4px",
    },
  },
  empty: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: theme.spacing(5, 2),
    color: theme.palette.text.secondary,
    minHeight: 180,
    textAlign: "center",
  },
  emptyIcon: {
    fontSize: 48,
    marginBottom: theme.spacing(1.5),
    opacity: 0.3,
  },
  loading: {
    display: "flex",
    justifyContent: "center",
    padding: theme.spacing(2),
  },
  footer: {
    padding: theme.spacing(1.5),
    borderTop: `1px solid ${theme.palette.divider}`,
    textAlign: "center",
  },
  viewAll: {
    fontSize: "0.875rem",
    color: theme.palette.primary.main,
    cursor: "pointer",
    "&:hover": { textDecoration: "underline" },
  },
}));

const ActivityNotificationsPopover = () => {
  const classes = useStyles();
  const history = useHistory();
  const { user } = useContext(AuthContext);
  const socketManager = useContext(SocketContext);
  const { list, completeSchedule, toggleTaskStatus } = useMyActivities();
  const authCompanyId = user && user.companyId;

  const [anchorEl, setAnchorEl] = useState(null);
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(false);
  const mountedRef = useRef(true);

  const fetchActivities = useCallback(async () => {
    setLoading(true);
    try {
      const data = await list();
      if (mountedRef.current) {
        setActivities(data?.activities || []);
      }
    } catch {
      /* silent */
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, [list]);

  useEffect(() => {
    mountedRef.current = true;
    fetchActivities();
    return () => {
      mountedRef.current = false;
    };
  }, [fetchActivities]);

  useEffect(() => {
    const companyId = authCompanyId || localStorage.getItem("companyId");
    if (!companyId) return () => {};
    const socket = socketManager.getSocket(companyId);
    if (!socket) return () => {};

    const refresh = () => fetchActivities();
    const taskEvent = `company-${companyId}-task`;
    const scheduleEvent = `company-${companyId}-schedule`;

    socket.on(taskEvent, refresh);
    socket.on(scheduleEvent, refresh);
    return () => {
      try {
        socket.off(taskEvent, refresh);
        socket.off(scheduleEvent, refresh);
      } catch {
        /* noop */
      }
    };
  }, [socketManager, fetchActivities, authCompanyId]);

  const handleOpen = (event) => {
    setAnchorEl(event.currentTarget);
    fetchActivities();
  };

  const handleClose = () => setAnchorEl(null);

  const handleComplete = useCallback(
    async (activity) => {
      if (activity.kind === "schedule") {
        await completeSchedule(activity.schedule.id);
      } else if (
        activity.task?.type === "message" &&
        activity.task?.scheduleId
      ) {
        await completeSchedule(activity.task.scheduleId);
      } else {
        await toggleTaskStatus(activity.task.id, "done");
      }
      setActivities((prev) =>
        prev.filter((a) =>
          a.kind === activity.kind &&
          (a.schedule?.id === activity.schedule?.id ||
            a.task?.id === activity.task?.id)
            ? false
            : true
        )
      );
    },
    [completeSchedule, toggleTaskStatus]
  );

  const handleNavigate = useCallback(
    (activity) => {
      handleClose();
      if (activity.kind === "schedule") {
        const ticketId = activity.schedule?.ticket?.id;
        if (ticketId) history.push(`/tickets/${ticketId}`);
        return;
      }
      const task = activity.task;
      if (task?.kanbanCard?.id) {
        const params = new URLSearchParams({
          cardId: String(task.kanbanCard.id),
        });
        if (task.kanbanCard.kanbanBoardId) {
          params.set("boardId", String(task.kanbanCard.kanbanBoardId));
        }
        history.push(`/kanban?${params.toString()}`);
        return;
      }
      if (task?.ticket?.id || task?.ticketId) {
        history.push(`/tickets/${task.ticket?.id || task.ticketId}`);
      }
    },
    [history]
  );

  const open = Boolean(anchorEl);
  const id = open ? "activity-notifications-popover" : undefined;
  const count = activities.length;

  return (
    <>
      <Tooltip title={i18n.t("mainDrawer.appBar.activities.title") || "Lembretes"}>
        <IconButton
          aria-describedby={id}
          className={classes.iconButton}
          onClick={handleOpen}
        >
          <Badge
            badgeContent={count}
            color="secondary"
            invisible={count === 0}
            className={classes.badge}
            max={99}
          >
            <AssignmentTurnedInIcon className={classes.iconButtonLabel} />
          </Badge>
        </IconButton>
      </Tooltip>

      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        transformOrigin={{ vertical: "top", horizontal: "center" }}
        classes={{ paper: classes.popoverPaper }}
      >
        <Box className={classes.header}>
          <Typography className={classes.headerTitle}>
            <AssignmentTurnedInIcon fontSize="small" />
            {i18n.t("mainDrawer.appBar.activities.title") || "Lembretes"}
          </Typography>
        </Box>

        <List className={classes.list}>
          {loading && activities.length === 0 ? (
            <Box className={classes.loading}>
              <CircularProgress size={24} />
            </Box>
          ) : activities.length === 0 ? (
            <Box className={classes.empty}>
              <AssignmentTurnedInIcon className={classes.emptyIcon} />
              <Typography variant="body2">
                {i18n.t("mainDrawer.appBar.activities.empty") ||
                  "Nenhum lembrete para as próximas 24h 🎉"}
              </Typography>
            </Box>
          ) : (
            activities.map((activity) => (
              <ActivityNotificationItem
                key={`${activity.kind}-${
                  activity.kind === "schedule"
                    ? activity.schedule.id
                    : activity.task.id
                }`}
                activity={activity}
                onComplete={handleComplete}
                onNavigate={handleNavigate}
              />
            ))
          )}
        </List>

        {activities.length > 0 && (
          <Box className={classes.footer}>
            <Typography
              className={classes.viewAll}
              onClick={() => {
                handleClose();
                history.push("/schedules");
              }}
            >
              {i18n.t("mainDrawer.appBar.activities.viewAll") ||
                "Ver todas as atividades"}
            </Typography>
          </Box>
        )}
      </Popover>
    </>
  );
};

export default ActivityNotificationsPopover;
